import { DayOfWeek } from "@src/appV2/InterviewSettings/constants";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { type PlacementData } from "../types/fetchPlacements.schema";

export function getStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case "open": {
      return "success";
    }

    case "draft": {
      return "default";
    }

    case "archived":
    case "closed": {
      return "warning";
    }

    default: {
      return "default";
    }
  }
}

export function formatPayRange(payRange: PlacementData["attributes"]["payRange"]) {
  const minPay = payRange?.minPay?.amountInMinorUnits;
  const maxPay = payRange?.maxPay?.amountInMinorUnits;

  if (!minPay && !maxPay) {
    return "Pay range: N/A";
  }

  if (minPay && maxPay) {
    return `Pay range: ${formatCentsAsUsd(minPay)} /hr - ${formatCentsAsUsd(maxPay)} /hr`;
  }

  return `Pay: ${formatCentsAsUsd(minPay ?? maxPay ?? 0)} /hr`;
}

export const DAY_TYPES = ["weekday", "weekend"] as const;
export type DayType = typeof DAY_TYPES[number];
const dayTypesMapping = {
  weekday: [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
  ],
  weekend: [DayOfWeek.SATURDAY, DayOfWeek.SUNDAY],
};

export function mapToDayType(daysOfWeek: DayOfWeek[]): DayType[] {
  const dayTypes: DayType[] = [];

  const daysCount = daysOfWeek.reduce(
    (accumulate, currentValue) => {
      if (dayTypesMapping.weekday.includes(currentValue)) {
        return { ...accumulate, weekday: accumulate.weekday + 1 };
      }

      if (dayTypesMapping.weekend.includes(currentValue)) {
        return { ...accumulate, weekend: accumulate.weekend + 1 };
      }

      return accumulate;
    },
    { weekend: 0, weekday: 0 }
  );

  if (daysCount.weekday === 5) {
    dayTypes.push("weekday");
  }

  if (daysCount.weekend === 2) {
    dayTypes.push("weekend");
  }

  return dayTypes;
}

export function mapFromDayType(dayTypes: string[]): DayOfWeek[] {
  const daysOfWeek: DayOfWeek[] = [];

  if (dayTypes.includes("weekday")) {
    daysOfWeek.push(...dayTypesMapping.weekday);
  }

  if (dayTypes.includes("weekend")) {
    daysOfWeek.push(...dayTypesMapping.weekend);
  }

  return daysOfWeek;
}
